import {Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {ComponentType} from '../../_models/component-type';
import {EventType} from '../../_models/event-type';
import {ApiService} from '../../services/api/api.service';
import {NotificationService} from '../../services/notification.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {concatMap, Observable, Subject, timer} from 'rxjs';
import {finalize, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {
    ComponentTypeComponentTypeLinkRequest,
    EventTypeComponentTypeLinkRequest
} from '../../_models/component-type-component-type';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {ComponentTypeDataService} from "../../data/component-type-data.service";
import {MatSnackBarRef} from "@angular/material/snack-bar";
import {ConfirmSnackbarComponent} from "../../notifications/snackbar/confirm-snackbar/confirm-snackbar.component";
import {deepCopy} from "../../lib/utils";
import {ModelID} from "../../_typing/generic-types";
import {COMMON_FORM_IMPORTS, COMMON_TABLE_IMPORTS} from "../../shared/import-groups/Common";
import {
    SelectSearchComponentComponent
} from "../../components/select-search-api/single/select-search-component.component";
import {FormulaBuilderRelationshipRulesComponent} from "./formula-builder/formula-builder-relationship-rules.component";
import {CommonModule} from "@angular/common";

type Relationship = {
    id?: string;
    type: 'component' | 'event' | (string & {});
    first_type_id: string;
    second_type_id: string;
    first_type_name: string;
    second_type_name: string;
    automatic_linking_rule: string | null;
    automatic_unlink: boolean;
    second_is_unique: boolean;
    first_is_unique: boolean;
    isNew?: boolean;
    didSwap?: boolean;
}

@Component({
    selector: 'component-relationship-form',
    templateUrl: './component-relationship-form.html',
    styleUrls: ['./component-relationship-form.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        COMMON_FORM_IMPORTS,
        COMMON_TABLE_IMPORTS,
        SelectSearchComponentComponent,
        FormulaBuilderRelationshipRulesComponent,
        CommonModule
    ],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({height: '0px', minHeight: '0'})),
            state('expanded', style({height: '*'})),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ]
})
export class ComponentRelationshipForm implements OnInit, OnDestroy {
    @Input() parentComponentType: ComponentType;

    relationshipForm: FormGroup;
    relationshipTypes = ['component', 'event'];
    selectedType: string = 'component';
    secondObject: ComponentType | EventType | null = null;

    availableComponentTypesFilter: any;
    availableEventTypesFilter: any;
    refreshType:boolean;
    showFormulaBuilder = false;
    _currentFormula: string | null = null;
    editingRelationship: Relationship | null = null;

    // Table configuration
    displayedColumns = [
        'actions',
        'type',
        'firstTypeName',
        'secondTypeName',
        'firstIsUnique',
        'secondIsUnique',
        'automaticUnlink',
        'automaticLinkingRule'
    ];
    dataSource: MatTableDataSource<any>;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    expandedElement: any | null = null;

    // State management
    relationships: Relationship[] = [];
    isLoading = false;

    private readonly onDestroy = new Subject<void>();

    constructor(
        private api: ApiService,
        private fb: FormBuilder,
        private notification: NotificationService,
        private componentTypeData: ComponentTypeDataService
    ) {
    }

    ngOnInit() {
        this.initForm();
        this._updateAvailableTypesFilter();
        this.setupSecondObjectSubscription();
        this.initializeTable();
        this.loadExistingRelationships();
    }

    private initForm() {
        this.relationshipForm = this.fb.group({
            relationType: ['component', Validators.required],
            secondComponentTypeId: [''],
            eventTypeId: [''],
            isFirstComponent: [true],
            firstIsUnique: [false],
            secondIsUnique: [false],
            hasOneEvent: [false],
            hasOneComponent: [false],
            automaticLinkingRule: [''],
            automaticUnlink: [false]
        });

        // Update validation based on relationship type
        this.relationshipForm.get('relationType').valueChanges.subscribe(type => {
            this.selectedType = type;
            this.updateFormValidation(type);
            this.secondObject = null; // Reset second object when type changes
        });
    }

    private setupSecondObjectSubscription() {
        // Watch for changes in the second object (component or event)
        this.relationshipForm.get('secondComponentTypeId').valueChanges.subscribe(id => {
            if (this.selectedType === 'component' && id) {
                this.api.component_type.getById(id).subscribe(
                    response => this.secondObject = response.data
                );
            }
        });

        this.relationshipForm.get('eventTypeId').valueChanges.subscribe(id => {
            if (this.selectedType === 'event' && id) {
                this.api.event_type.getById(id).subscribe(
                    response => this.secondObject = response.data
                );
            }
        });
    }

    onFormulaChange(formula: string) {
        if (this.editingRelationship) {
            this._currentFormula = formula;
            this.editingRelationship.automatic_linking_rule = formula;
        }
    }

    private _updateAvailableTypesFilter() {
        if (!this.parentComponentType?.id) return;
        this.availableComponentTypesFilter = [
            {name: 'id', op: 'ne', val: this.parentComponentType.id},
            {not: {name: 'component_type_component_type', op: 'any', val: {name: 'first_component_type', op:'has', val: {name: 'id', op: 'eq', val: this.parentComponentType.id}}}},
            {not: {name: 'component_type_component_type', op: 'any', val: {name: 'second_component_type', op:'has', val: {name: 'id', op: 'eq', val: this.parentComponentType.id}}}}
        ];
        const componentRelationshipFilters = this.componentTypeData.getComponentRelationshipFilters(this.parentComponentType.id);
        this.availableEventTypesFilter = [{not: componentRelationshipFilters[0]}, componentRelationshipFilters[1]]
        this.availableEventTypesFilter = [
            {not: {name: 'component_types', op: 'any', val: {name: 'id', op: 'eq', val: this.parentComponentType.id}}}
        ];
    }

    private updateFormValidation(type: string) {
        const form = this.relationshipForm;
        if (type === 'component') {
            form.get('secondComponentTypeId').setValidators(Validators.required);
            form.get('eventTypeId').clearValidators();
        } else {
            form.get('eventTypeId').setValidators(Validators.required);
            form.get('secondComponentTypeId').clearValidators();
        }
        form.updateValueAndValidity();
    }

    startEdit(relationship: Relationship) {
        if (this.editingRelationship && this.editingRelationship !== relationship) {
            this.notification.openError('Please save or cancel the current edit first');
            return;
        }
        this.editingRelationship = relationship;
        this.showFormulaBuilder = false;
        this._currentFormula = relationship?.automatic_linking_rule || null;
        this.secondObject = null;
    }

    cancelEdit() {
        if (this.editingRelationship?.isNew) {
            this.relationships = this.relationships.filter(r => r !== this.editingRelationship);
            this.dataSource.data = this.relationships;
        }
        this.editingRelationship = null;
        this.showFormulaBuilder = false;
        this._currentFormula = null;
        this.expandedElement = null;
    }

    addNewRelationship() {
        if (this.editingRelationship) {
            this.notification.openError('Please save or cancel the current edit first');
            return;
        }
        const newRelationship = {
            type: 'component',
            first_type_id: this.parentComponentType.id,
            first_type_name: this.parentComponentType.attributes?.name,
            second_type_id: null,
            second_type_name: null,
            automatic_linking_rule: null,
            automatic_unlink: false,
            first_is_unique: false,
            second_is_unique: false,
            isNew: true
        };
        this.relationships = [newRelationship, ...this.relationships];
        this.dataSource.data = this.relationships;
        this.startEdit(newRelationship);
    }

    saveRelationship(relationship: Relationship) {
        if (relationship.type === 'component') {
            const unswap = relationship.didSwap || false;
            const payload = new ComponentTypeComponentTypeLinkRequest({
                first_component_type_id: unswap ? relationship.second_type_id : relationship.first_type_id,
                second_component_type_id: unswap ? relationship.first_type_id : relationship.second_type_id,
                automatic_linking_rule: this._currentFormula,
                automatic_unlink: relationship.automatic_unlink,
                first_is_unique: unswap ? relationship.second_is_unique : relationship.first_is_unique,
                second_is_unique: unswap ? relationship.first_is_unique : relationship.second_is_unique
            });

            this.api.configureComponentTypeComponentTypeLink(payload).subscribe({
                next: () => {
                    this.notification.openSuccess('Relationship saved successfully');
                    this.loadExistingRelationships();
                    this.cancelEdit();
                },
                error: () => {
                    this.notification.openError('Error saving relationship');
                }
            });
        } else {
            const payload = new EventTypeComponentTypeLinkRequest({
                component_type_id: relationship.first_type_id,
                event_type_id: relationship.second_type_id,
                automatic_linking_rule: this._currentFormula,
                automatic_unlink: relationship.automatic_unlink,
                /**
                 * From Component Perspective:
                 *  - first     -> Component
                 *  - second    -> Event
                 * so first_is_unique maps to has_one_event, second_is_unique maps to has_one_component
                 */
                has_one_event: relationship.first_is_unique,
                has_one_component: relationship.second_is_unique
            });

            this.api.configureEventTypeComponentTypeLink(payload).subscribe({
                next: () => {
                    this.notification.openSuccess('Relationship saved successfully');
                    this._updateEventTypeRelationship(payload.event_type_id, 'add');
                    this.loadExistingRelationships();
                    this.cancelEdit();
                },
                error: () => {
                    this.notification.openError('Error saving relationship');
                }
            });
        }
    }

    private _updateEventTypeRelationship(eventTypeId: ModelID, actionType: 'add' | 'delete'): void {
        if (eventTypeId) {
            const currentEventTypeStub = this.parentComponentType.relationships.event_types.data?.find(e => e.id === eventTypeId);
            if (actionType === 'add' && !currentEventTypeStub) {
                this.parentComponentType.relationships.event_types.data = (this.parentComponentType.relationships.event_types.data || []).concat({
                    id: eventTypeId,
                    type: 'event_type'
                })
                return;
            }
            if (actionType === 'delete' && currentEventTypeStub) {
                this.parentComponentType.relationships.event_types.data.splice(this.parentComponentType.relationships.event_types.data.indexOf(currentEventTypeStub), 1);
            }
        }
    }

    deleteRelationship(relationship: Relationship): void {
        let payload: any;
        let confirmNotification: MatSnackBarRef<ConfirmSnackbarComponent>;
        confirmNotification = this.notification.openConfirm('Are you sure you want to delete this relationship? This action can not be undone.');
        confirmNotification.onAction().pipe(concatMap(() => {
            let $obs: Observable<any>;
            if (relationship.type === 'component') {
                const isParentFirst = relationship.first_type_id === this.parentComponentType.id;
                payload = {
                    first_component_type_id: isParentFirst ? relationship.first_type_id : relationship.second_type_id,
                    second_component_type_id: isParentFirst ? relationship.second_type_id : relationship.first_type_id
                }
                $obs = this.api.deleteComponentTypeComponentTypeLink(payload);
            } else {
                payload = {
                    component_type_id: relationship.first_type_id,
                    event_type_id: relationship.second_type_id
                };
                $obs = this.api.deleteEventTypeComponentTypeLink(payload);
            }
            return $obs;
        }), take(1))
            .subscribe({
                next: () => {
                    this.notification.openSuccess('Relationship deleted successfully');
                    this._updateEventTypeRelationship(payload.event_type_id, 'delete');
                    this.loadExistingRelationships();
                },
                error: () => {
                    this.notification.openError('Error deleting relationship');
                }
            });
    }

    onSecondObjectChange(relationship: Relationship, selectedObject: ComponentType | EventType) {
        relationship.second_type_id = selectedObject.id;
        relationship.second_type_name = selectedObject.attributes?.name;
        this.secondObject = selectedObject;
    }

    onTypeChange(relationship: Relationship, newType: 'component' | 'event') {
        relationship.type = newType;
        relationship.second_type_id = null;
        relationship.second_type_name = null;
    }

    configureAutomaticLinking(relationship: Relationship) {
        if (!relationship.second_type_id) {
            this.notification.openError('Please select a second type before configuring linking rules');
            return;
        }

        // First set up the second object
        if (relationship.type === 'component') {
            this.api.component_type.getById(relationship.second_type_id).subscribe({
                next: (response) => {
                    this.secondObject = response.data;
                    this.editingRelationship = relationship;
                    this.showFormulaBuilder = true;
                    this._currentFormula = relationship.automatic_linking_rule;
                    this.expandedElement = relationship;
                },
                error: () => {
                    this.notification.openError('Error loading second component type');
                }
            });
        } else {
            this.api.event_type.getById(relationship.second_type_id).subscribe({
                next: (response) => {
                    this.secondObject = response.data;
                    this.editingRelationship = relationship;
                    this.showFormulaBuilder = true;
                    this._currentFormula = relationship.automatic_linking_rule;
                    this.expandedElement = relationship;
                },
                error: () => {
                    this.notification.openError('Error loading event type');
                }
            });
        }
    }

    private initializeTable() {
        this.dataSource = new MatTableDataSource(this.relationships);
        setTimeout(() => {
            this.dataSource.paginator = this.paginator;
        });
    }

    private loadExistingRelationships() {
        if (!this.parentComponentType?.id) return;

        this.isLoading = true;
        this.api.getComponentTypeLinks({
            component_type_id: this.parentComponentType.id
        })
            .pipe(
                finalize(() => this.isLoading = false),
                takeUntil(this.onDestroy)
            )
            .subscribe({
                next: (response) => {
                    this.relationships = response.links.map(rel => {
                        const isEventRel = this.parentComponentType.relationships.event_types.data?.find(e => [rel.first_type_id, rel.second_type_id].includes(e.id));
                        const isParentFirst = rel.first_type_id === this.parentComponentType.id;

                        if (isEventRel) {
                            return {
                                type: 'event',
                                first_type_id: rel.second_type_id,
                                first_type_name: rel.second_type_name,
                                second_type_id: rel.first_type_id,
                                second_type_name: rel.first_type_name,
                                // Backend returns first_is_unique = has_one_event, second_is_unique = has_one_component
                                // But UI shows component first, event second, so swap them
                                first_is_unique: rel.first_is_unique,  // Component's uniqueness
                                second_is_unique: rel.second_is_unique,  // Event's uniqueness
                                automatic_linking_rule: rel.automatic_linking_rule,
                                automatic_unlink: rel.automatic_unlink,
                                didSwap: !isParentFirst
                            };
                        }

                        return {
                            type: 'component',
                            first_type_id: isParentFirst ? rel.first_type_id : rel.second_type_id,
                            first_type_name: isParentFirst ? rel.first_type_name : rel.second_type_name,
                            second_type_id: isParentFirst ? rel.second_type_id : rel.first_type_id,
                            second_type_name: isParentFirst ? rel.second_type_name : rel.first_type_name,
                            first_is_unique: isParentFirst ? rel.first_is_unique : rel.second_is_unique,
                            second_is_unique: isParentFirst ? rel.second_is_unique : rel.first_is_unique,
                            automatic_linking_rule: rel.automatic_linking_rule,
                            automatic_unlink: rel.automatic_unlink,
                            didSwap: !isParentFirst
                        };
                    });
                    this.dataSource.data = this.relationships;
                },
                error: (error) => {
                    this.notification.openError('Error loading relationships');
                    console.error('Error loading relationships:', error);
                }
            });
    }

    isEditing(rel: Relationship) {
        return this.editingRelationship === rel;
    }

    ngOnDestroy() {
        this.onDestroy.next();
        this.onDestroy.complete();
    }
}
